/**
 * Voice Cloning API Service
 *
 * Handles all API calls for voice cloning feature.
 */

import { buildApiUrl, getApiBaseUrl } from './urlResolver';
import type {
  IPlayableVoiceAudio,
  IVoiceCreateResponse,
  IVoiceDeleteResponse,
  IVoiceInfo,
  IVoiceListResponse,
  IVoiceUploadRequest,
  UploadProgressCallback,
} from '../types/voice.types';
import { FILE_SIZE_LIMITS, MB } from '@/constants/fileSize';
import { TIMEOUTS } from '@/constants/timings';
import { normalizeVoiceListResponse, normalizeVoiceInfo } from '../utils/apiNormalizers';
import { extractResponseErrorMessage, unwrapEnvelope } from '../utils/httpUtils';
import { isE2ETestMode } from '../utils/apiMode';

// Constants (should match backend)
const VOICE_MIN_DURATION_SECONDS = 1;
const VOICE_MAX_DURATION_SECONDS = 25;
const VOICE_OPTIMAL_MIN_DURATION = 10;
const VOICE_OPTIMAL_MAX_DURATION = 15;
const VOICE_MAX_FILE_SIZE_MB = FILE_SIZE_LIMITS.VOICE_MAX_MB;
const VOICE_ALLOWED_EXTENSIONS = ['.wav', '.mp3', '.webm'];

/**
 * Seek offset (seconds) used to force the media element to scan to the real end
 * of a stream whose container declares no duration. Any value far beyond every
 * plausible duration works; the element clamps to the true end and then reports
 * it. Not a timing/timeout value, so it belongs here and not in constants/timings.
 */
const SEEK_TO_END_SECONDS = 1e101;

/**
 * Voice API client class.
 */
class VoiceApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${getApiBaseUrl()}/api/v1/voices`;
  }

  /**
   * Get authorization headers.
   * Returns empty headers if Firebase is not available or user is not authenticated.
   * Local development works without authentication.
   */
  private async getAuthHeaders(): Promise<Headers> {
    const headers = new Headers();
    headers.set('X-Requested-With', 'XMLHttpRequest');
    // Inject E2E bypass header so serverless auth middleware accepts the
    // request when `_E2E_TEST_MODE=true` on Cloud Run. Independent of the
    // Firebase token branch below — never leak to real users.
    if (isE2ETestMode()) {
      headers.set('X-E2E-Test', 'true');
    }

    try {
      // Use the Firebase client singleton to ensure we get the correct auth state
      const { firebaseClient } = await import('./firebase');
      const token = await firebaseClient.getIdToken();

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        console.debug('[VoiceAPI] Got Firebase token successfully');
      } else {
        // Log warning for debugging (not an error - local dev works without auth)
        console.warn('[VoiceAPI] No Firebase token available - user may need to sign in.');
      }
    } catch (err) {
      // Firebase not configured - OK for local development
      console.error('[VoiceAPI] Firebase error getting token:', err);
    }

    return headers;
  }

  /**
   * Turn a failed response body into the clearest message the backend supplied.
   *
   * The standard envelope carries `error` as an OBJECT ({code, message, details}),
   * so reading `body.error` straight into `new Error()` renders "[object Object]"
   * and the real reason is lost. The shared extractor handles that shape, plus
   * FastAPI's own {detail: ...} bodies - which every voice endpoint can return,
   * because the auth dependency raises HTTPException(401, detail=...).
   */
  private errorMessageFrom(body: unknown, fallbackMessage: string): string {
    if (typeof body !== 'object' || body === null) {
      return fallbackMessage;
    }
    return extractResponseErrorMessage(body as Record<string, unknown>) ?? fallbackMessage;
  }

  /**
   * Make a voice's audio_url usable by whoever receives it.
   *
   * The backend returns a ROOT-RELATIVE path ("/api/v1/voices/<id>/audio").
   * In the browser build that resolves fine, because the page origin is the
   * API origin. In the desktop app the page is loaded from
   * file:///Applications/Asgard Studio.app/... so the same string resolves to
   * file:///api/v1/voices/<id>/audio, which does not exist — `new Audio(...)`
   * fires onerror and playback silently does nothing.
   *
   * Resolved here, in the one service that owns these endpoints, so every
   * consumer (the voice list's play button, the TTS voice options, previews)
   * gets a URL it can actually load — rather than each of them prefixing a
   * base URL for itself.
   *
   * Absolute URLs are returned untouched: the cloud build serves audio from
   * signed storage URLs on another origin.
   */
  private toPlayableAudioUrl(audioUrl: string | null): string | null {
    if (!audioUrl || /^https?:\/\//i.test(audioUrl)) {
      return audioUrl;
    }
    return buildApiUrl(audioUrl);
  }

  /**
   * Upload a voice audio file.
   *
   * @param file - Audio file to upload
   * @param metadata - Voice metadata (name, language_hint)
   * @param onProgress - Optional progress callback
   * @returns Created voice response
   */
  async uploadVoice(
    file: File,
    metadata: IVoiceUploadRequest,
    onProgress?: UploadProgressCallback
  ): Promise<IVoiceCreateResponse> {
    const headers = await this.getAuthHeaders();

    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name);
    if (metadata.language_hint) {
      formData.append('language_hint', metadata.language_hint);
    }

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Progress handler
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };
      }

      // Completion handler
      xhr.onload = () => {
        let body: unknown;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          reject(new Error(`Upload failed: unreadable response (status ${xhr.status})`));
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            // Throws loudly if the body is not the standard {success, data} envelope
            resolve(unwrapEnvelope<IVoiceCreateResponse>(body));
          } catch (e) {
            reject(e instanceof Error ? e : new Error('Failed to parse response'));
          }
          return;
        }

        reject(new Error(this.errorMessageFrom(body, `Upload failed with status ${xhr.status}`)));
      };

      // Error handler
      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      // Send request
      xhr.open('POST', `${this.baseUrl}/upload`);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

      // Add auth header - Headers.forEach iterates as (value, key)
      headers.forEach((value, key) => {
        console.debug(`[VoiceAPI] Setting header: ${key}=${value ? '[TOKEN]' : 'empty'}`);
        xhr.setRequestHeader(key, value);
      });

      // Debug: log if no Authorization header was set
      if (!headers.has('Authorization')) {
        console.error('[VoiceAPI] WARNING: No Authorization header set for upload!');
      }

      xhr.send(formData);
    });
  }

  /**
   * List all voices for the current user.
   *
   * @param includeUrls - Whether to include audio URLs
   * @returns List of voices
   */
  async listVoices(includeUrls = false): Promise<IVoiceListResponse> {
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    const url = new URL(this.baseUrl);
    if (includeUrls) {
      url.searchParams.set('include_urls', 'true');
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(this.errorMessageFrom(body, 'Failed to list voices'));
    }

    // Unwrap the standard envelope, then normalize
    const unwrapped = unwrapEnvelope<IVoiceListResponse>(await response.json());
    const list = normalizeVoiceListResponse(unwrapped);
    return {
      ...list,
      voices: list.voices.map((voice) => ({
        ...voice,
        audio_url: this.toPlayableAudioUrl(voice.audio_url),
      })),
    };
  }

  /**
   * Get details for a specific voice.
   *
   * @param voiceId - Voice ID
   * @returns Voice info
   */
  async getVoice(voiceId: string): Promise<IVoiceInfo> {
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    const response = await fetch(`${this.baseUrl}/${voiceId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(this.errorMessageFrom(body, 'Voice not found'));
    }

    // Unwrap the standard envelope, then normalize
    const unwrapped = unwrapEnvelope<IVoiceInfo>(await response.json());
    const normalized = normalizeVoiceInfo(unwrapped);
    if (!normalized) {
      throw new Error('Invalid voice data received from API');
    }
    return { ...normalized, audio_url: this.toPlayableAudioUrl(normalized.audio_url) };
  }

  /**
   * Get audio URL for a voice.
   *
   * @param voiceId - Voice ID
   * @returns Audio URL
   */
  async getVoiceAudioUrl(voiceId: string): Promise<string> {
    const voice = await this.getVoice(voiceId);
    if (!voice.audio_url) {
      throw new Error('Audio URL not available');
    }
    return voice.audio_url;
  }

  /**
   * Is this URL served by our own API, rather than by storage?
   *
   * The two need opposite handling. Our endpoint wants the Authorization header
   * and cannot be pointed at by a media element (see loadVoiceAudio). A
   * pre-signed storage URL already carries its credentials in the query string
   * and MUST NOT receive an Authorization header, which would invalidate it.
   */
  private isOwnApiUrl(url: string): boolean {
    return url.startsWith(this.baseUrl);
  }

  /**
   * Load a voice's audio in a form a media element can actually play.
   *
   * Pointing an element straight at our own endpoint does not work, and fails
   * in a way that looks like a broken file: the request reaches the backend and
   * returns 200, and the browser then throws the response away. A media element
   * issues a no-cors request, and every API response carries
   * `Cross-Origin-Resource-Policy: same-origin`
   * (shared/middleware/security_headers.py), which the browser enforces on
   * exactly those requests. The desktop page is served from file://, so it is
   * never same-origin with http://localhost:5001 and the audio is dropped. The
   * same mechanism already broke b-roll playback once - see
   * tests/test_api_server/test_broll_corp_headers_regression.py.
   *
   * Relaxing the header for this route is not the fix: every
   * CROSS_ORIGIN_RESOURCE_PATHS entry must also be public
   * (tests/test_api_server/test_avatar_public_access.py), and /api/v1/voices
   * carries the user's own recordings plus upload and delete.
   *
   * So we fetch the bytes ourselves - a CORS request, which CORP does not touch
   * and which can carry the Authorization header the cloud build requires - and
   * hand back a blob. This is what VoicePreviewButton already does for TTS
   * samples; cloned voices now play through the same mechanism.
   *
   * @param voice - Voice whose audio_url came from listVoices/getVoice
   * @returns The playable source; the caller must call `release()` when done
   */
  async loadVoiceAudio(voice: IVoiceInfo): Promise<IPlayableVoiceAudio> {
    const audioUrl = voice.audio_url;
    if (!audioUrl) {
      throw new Error(`Voice ${voice.voice_id} has no audio URL`);
    }

    // Pre-signed storage URL (cloud build): loadable as-is, and fetching it with
    // our Authorization header attached would break the signature.
    if (!this.isOwnApiUrl(audioUrl)) {
      return { src: audioUrl, release: () => {} };
    }

    const headers = await this.getAuthHeaders();
    const response = await fetch(audioUrl, { headers });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(
        this.errorMessageFrom(body, `Failed to load voice audio (status ${response.status})`)
      );
    }

    const objectUrl = URL.createObjectURL(await response.blob());
    return {
      src: objectUrl,
      release: () => URL.revokeObjectURL(objectUrl),
    };
  }

  /**
   * Delete a voice.
   *
   * @param voiceId - Voice ID
   * @returns Delete response
   */
  async deleteVoice(voiceId: string): Promise<IVoiceDeleteResponse> {
    const headers = await this.getAuthHeaders();
    headers.set('Content-Type', 'application/json');

    const response = await fetch(`${this.baseUrl}/${voiceId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(this.errorMessageFrom(body, 'Failed to delete voice'));
    }

    // Unwrap the standard envelope
    return unwrapEnvelope<IVoiceDeleteResponse>(await response.json());
  }

  /**
   * Validate audio file before upload (client-side).
   *
   * @param file - Audio file to validate
   * @returns Validation result
   */
  async validateAudioFile(file: File): Promise<{
    isValid: boolean;
    errorMessage?: string;
    warningMessage?: string;
    durationSeconds?: number;
  }> {
    // Check file extension
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!VOICE_ALLOWED_EXTENSIONS.includes(ext)) {
      return {
        isValid: false,
        errorMessage: `Invalid file format. Allowed: ${VOICE_ALLOWED_EXTENSIONS.join(', ')}`,
      };
    }

    // Check file size
    const sizeMB = file.size / MB;
    if (sizeMB > VOICE_MAX_FILE_SIZE_MB) {
      return {
        isValid: false,
        errorMessage: `File too large. Maximum size: ${VOICE_MAX_FILE_SIZE_MB}MB`,
      };
    }

    // Check duration
    try {
      const duration = await this.getAudioDuration(file);

      if (duration < VOICE_MIN_DURATION_SECONDS || duration > VOICE_MAX_DURATION_SECONDS) {
        return {
          isValid: false,
          errorMessage: `Audio must be between ${VOICE_MIN_DURATION_SECONDS} and ${VOICE_MAX_DURATION_SECONDS} seconds. Your file is ${duration.toFixed(1)} seconds.`,
          durationSeconds: duration,
        };
      }

      // Check for optimal duration (warning, not error)
      let warningMessage: string | undefined;
      if (duration < VOICE_OPTIMAL_MIN_DURATION || duration > VOICE_OPTIMAL_MAX_DURATION) {
        warningMessage = `For best results, use audio between ${VOICE_OPTIMAL_MIN_DURATION}-${VOICE_OPTIMAL_MAX_DURATION} seconds.`;
      }

      return {
        isValid: true,
        durationSeconds: duration,
        warningMessage,
      };
    } catch {
      return {
        isValid: false,
        errorMessage: 'Could not read audio file. File may be corrupted.',
      };
    }
  }

  /**
   * Get audio duration by reading metadata off an HTMLAudioElement.
   *
   * MUST NOT use the Web Audio API. `AudioContext.decodeAudioData` hard-crashes
   * (SIGSEGV, exit code 11) the renderer of the Chromium 120 build shipped with
   * Electron 28.3.3, reproduced for every codec tested - plain PCM wav and
   * webm/opus alike. The process dies inside the decode call, so no try/catch
   * can ever run: the crash can only be avoided, never handled. An ESLint
   * `no-restricted-syntax` rule bans `decodeAudioData` across the frontend so
   * this cannot silently come back.
   *
   * MediaRecorder webm carries no duration in its header, so `duration` reads
   * back as Infinity until the element is forced to scan to the real end by
   * seeking far past it.
   *
   * @param file - Audio file (.wav / .mp3 / .webm)
   * @returns Duration in seconds
   */
  private getAudioDuration(file: File): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      const objectUrl = URL.createObjectURL(file);
      let settled = false;

      // Number.isFinite() is false for both Infinity and NaN, the two values an
      // element reports when it cannot work out the duration.
      const isUsableDuration = (value: number): boolean => Number.isFinite(value);

      // Tears the element down and then settles the promise. Read any value off
      // `audio` BEFORE calling this: the load() below resets duration to NaN.
      const settle = (finish: () => void): void => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        URL.revokeObjectURL(objectUrl);
        audio.removeAttribute('src');
        audio.load();
        finish();
      };

      const timer = setTimeout(
        () => settle(() => reject(new Error('Timed out reading audio metadata'))),
        TIMEOUTS.MEDIA_METADATA
      );

      audio.onloadedmetadata = () => {
        const declaredDuration = audio.duration;
        if (isUsableDuration(declaredDuration)) {
          settle(() => resolve(declaredDuration));
          return;
        }

        // Duration unknown (MediaRecorder webm): seek past the end so the
        // element scans the stream, then read the duration it settles on.
        audio.ontimeupdate = () => {
          audio.ontimeupdate = null;
          const duration = audio.duration;
          audio.currentTime = 0;
          if (isUsableDuration(duration)) {
            settle(() => resolve(duration));
          } else {
            settle(() => reject(new Error('Could not determine audio duration')));
          }
        };
        audio.currentTime = SEEK_TO_END_SECONDS;
      };

      audio.onerror = () => settle(() => reject(new Error('Failed to load audio')));

      audio.src = objectUrl;
    });
  }
}

// Export singleton instance
export const voiceApi = new VoiceApiService();

// Export class for testing
export { VoiceApiService };

// Export constants for use in components
export {
  VOICE_MIN_DURATION_SECONDS,
  VOICE_MAX_DURATION_SECONDS,
  VOICE_OPTIMAL_MIN_DURATION,
  VOICE_OPTIMAL_MAX_DURATION,
  VOICE_MAX_FILE_SIZE_MB,
  VOICE_ALLOWED_EXTENSIONS,
};
